import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PlanBreakdown, UpgradeOption } from '@journeyapps-platform/types-charging-station';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useSystem } from '../../../../hooks/useSystem';
import { styled } from '../../../../theme';
import { getScrollbarCss } from '../../../../widgets/layout/scroll';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export interface EntryWidgetProps {
  entry: Omit<PlanBreakdown, 'info'>;
  info?: string[];
}

export const EntryWidget: React.FC<EntryWidgetProps> = ({ entry, info }) => {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <S.Entry
      onClick={() => {
        setCollapsed(!collapsed);
      }}
    >
      <S.EntryTop>
        <S.Check icon="check" />
        <S.EntryName>{entry.label}</S.EntryName>
        <S.FlexSpacer></S.FlexSpacer>
        {entry.max ? <S.Max>Max of {entry.max}</S.Max> : null}
        {info ? <S.Angle icon={collapsed ? 'plus' : 'minus'} /> : null}
      </S.EntryTop>
      {info && !collapsed ? (
        <S.EntryBottom>
          {info.map((i) => {
            return <S.EntryInfo key={i}>{i}</S.EntryInfo>;
          })}
        </S.EntryBottom>
      ) : null}
    </S.Entry>
  );
};

export interface UpgradeOptionWidgetProps {
  upgrade: UpgradeOption;
  selected: boolean;
}

export const UpgradeOptionWidget: React.FC<UpgradeOptionWidgetProps> = observer(({ upgrade, selected }) => {
  const system = useSystem();
  let image = '';
  if (upgrade.logo) {
    image = `data:image/png;base64,${upgrade.logo}`;
  }
  return (
    <S.Container selected={selected}>
      <S.ContainerInner>
        <S.TopContent>
          <S.Logo src={image} />
          <S.FlexSpacer />
          <S.PriceBlock>
            <S.PriceTop>
              <S.Total>{formatter.format(upgrade.total_pm / 100)}</S.Total>
              <S.TotalPm>/pm</S.TotalPm>
            </S.PriceTop>
          </S.PriceBlock>
        </S.TopContent>
        <S.BodyContent>
          {upgrade.breakdown.map((b) => {
            return <EntryWidget entry={b} key={b.key} info={b.info} />;
          })}
        </S.BodyContent>
        <S.BottomContent>
          <S.Terms>Terms and conditions apply</S.Terms>
          {upgrade.external_link ? (
            <S.Link href={upgrade.external_link} target="_blank">
              Click <span style={{ color: system.theme.plans.upgradeOption.accent }}>here</span> for more info
            </S.Link>
          ) : null}
        </S.BottomContent>
      </S.ContainerInner>
    </S.Container>
  );
});

export const EnterpriseUpgradeOptionWidget: React.FC<{ selected: boolean }> = observer(({ selected }) => {
  return (
    <S.Container selected={selected}>
      <S.ContainerInner>
        <S.TopContent>
          <S.EnterpriseName>Enterprise plan</S.EnterpriseName>
        </S.TopContent>
        <S.BodyContent>
          <EntryWidget
            entry={{
              label: 'Support with SLA',
              key: 'a',
              desc: ''
            }}
          />
          <EntryWidget
            entry={{
              label: 'Custom limits and usage',
              key: 'b',
              desc: ''
            }}
          />
          <EntryWidget
            entry={{
              label: 'Assigned customer success engineer',
              key: 'c',
              desc: ''
            }}
          />
          <EntryWidget
            entry={{
              label: 'SOC 2 report',
              key: 'c',
              desc: ''
            }}
          />
        </S.BodyContent>
        <S.BottomContent>
          <S.Terms>Terms and conditions apply</S.Terms>
        </S.BottomContent>
      </S.ContainerInner>
    </S.Container>
  );
});

namespace S {
  export const Container = styled.div<{ selected: boolean }>`
    background: ${(p) =>
      p.selected ? p.theme.plans.upgradeOption.selectedBackground : p.theme.plans.upgradeOption.unselectedBackground};
    border-radius: 30px;
    padding: 6px;
  `;

  export const ContainerInner = styled.div`
    background: ${(p) => p.theme.plans.upgradeOption.background};
    border-radius: 23px;
    padding: 10px;
  `;

  export const TopContent = styled.div`
    display: flex;
    align-items: center;
    padding: 10px;
  `;

  export const EnterpriseName = styled.div`
    color: ${(p) => p.theme.text.primary};
    font-weight: bold;
    font-size: 30px;
  `;

  export const Logo = styled.img`
    max-width: 300px;
  `;

  export const BodyContent = styled.div`
    padding: 10px;
    overflow: auto;
    max-height: 325px;
    ${(p) => getScrollbarCss(p.theme)};
  `;

  export const BottomContent = styled.div`
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 10px;
    margin-top: 10px;
  `;

  export const Terms = styled.div`
    font-size: 14px;
    color: ${(p) => p.theme.plans.upgradeOption.terms};
  `;

  export const Link = styled.a`
    font-size: 14px;
    text-decoration: none;
    color: ${(p) => p.theme.plans.upgradeOption.link};
  `;

  export const Entry = styled.div`
    padding: 8px;
    border-radius: 5px;
    background: ${(p) => p.theme.plans.upgradeOption.entryBackground};
    margin-bottom: 4px;
    cursor: pointer;

    &:last-of-type {
      margin-bottom: 0;
    }
  `;

  export const EntryTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 10px;
    padding: 5px;
    width: 100%;
  `;

  export const FlexSpacer = styled.div`
    flex-grow: 1;
  `;

  export const EntryBottom = styled.div`
    padding: 10px;
  `;

  export const Max = styled.div`
    color: ${(p) => p.theme.plans.upgradeOption.max};
    font-size: 14px;
  `;

  export const EntryInfo = styled.div`
    color: ${(p) => p.theme.plans.upgradeOption.entryInfo};
    font-size: 12px;
  `;

  export const EntryName = styled.div`
    font-size: 16px;
    font-weight: bold;
    color: ${(p) => p.theme.plans.upgradeOption.entryLabel};
  `;

  export const Check = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.plans.upgradeOption.accent};
    font-size: 17px;
  `;

  export const Total = styled.div`
    font-weight: bold;
    font-size: 30px;
    color: ${(p) => p.theme.plans.upgradeOption.total};
  `;

  export const Angle = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.plans.upgradeOption.meta};
    opacity: 0.2;
    font-size: 17px;
    width: 20px;
  `;

  export const TotalPm = styled.div`
    font-size: 15px;
    color: ${(p) => p.theme.plans.upgradeOption.total};
  `;

  export const PriceBlock = styled.div``;

  export const PriceTop = styled.div`
    display: flex;
    align-items: baseline;
  `;
}
